import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageHeader = styled.div`
  padding: 8rem 0 4rem;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to right bottom, var(--color-primary-dark) 49%, var(--color-white) 50%);
  }
`;

const PageHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 700px;
`;

const ServicesOverview = styled.section`
  padding: 6rem 0 3rem;
`;

const OverviewContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const OverviewTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const OverviewDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 800px;
  margin-bottom: 2rem;
`;

const ServiceSection = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 100px;
  
  &:nth-child(even) {
    background-color: var(--color-gray);
  }
`;

const ServiceContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    & > * {
      direction: ltr;
    }
    
    @media (max-width: 768px) {
      direction: ltr;
    }
  }
`;

const ServiceImageContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  height: 400px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const ServiceContent = styled.div``;

const ServiceTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  margin-bottom: 2rem;
  padding-left: 1.5rem;
`;

const ServiceFeature = styled.li`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  position: relative;
  
  &:before {
    content: '✓';
    color: var(--color-accent);
    position: absolute;
    left: -1.5rem;
    font-weight: bold;
  }
`;

const CtaButton = styled(Link)`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-primary-dark);
    color: var(--color-white);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ProcessSection = styled.section`
  padding: 6rem 0;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
`;

const ProcessContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ProcessTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const ProcessStep = styled.div`
  text-align: center;
  padding: 2rem;
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  background-color: var(--color-accent);
  color: var(--color-white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  line-height: 1.7;
  opacity: 0.9;
`;

const TestimonialsSection = styled.section`
  padding: 6rem 0;
`;

const TestimonialsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TestimonialsTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const TestimonialCard = styled.div`
  background-color: var(--color-white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 20px;
    font-size: 5rem;
    color: var(--color-accent);
    opacity: 0.2;
    font-family: Georgia, serif;
    line-height: 1;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
`;

const TestimonialClient = styled.div`
  display: flex;
  align-items: center;
`;

const ClientImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin-right: 1rem;
`;

const ClientInfo = styled.div``;

const ClientName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const ClientPosition = styled.p`
  font-size: 0.9rem;
  color: var(--color-accent);
`;

const CtaSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-accent);
  color: var(--color-white);
  text-align: center;
`;

const CtaContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CtaText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CtaWhiteButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: var(--color-white);
  color: var(--color-accent);
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-primary-dark);
    color: var(--color-white);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Services = () => {
  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>Our Services</PageTitle>
          <PageSubtitle>
            We provide strategic technical guidance and implementation expertise to help you navigate complex technology decisions.
          </PageSubtitle>
        </PageHeaderContent>
      </PageHeader>
      
      <ServicesOverview>
        <OverviewContainer>
          <OverviewTitle>How We Help You Succeed</OverviewTitle>
          <OverviewDescription>
            At Kriegerlabs, we combine deep technical expertise with strategic business acumen to help you make confident technology decisions. Our services are designed to bring clarity to complex challenges and ensure your technology investments deliver maximum value.
          </OverviewDescription>
        </OverviewContainer>
      </ServicesOverview>
      
      <ServiceSection id="architecture">
        <ServiceContainer>
          <ServiceImageContainer>
            <ServiceImage style={{ backgroundImage: 'url("/images/architecture.jpg")' }} />
          </ServiceImageContainer>
          
          <ServiceContent>
            <ServiceTitle>Architecture</ServiceTitle>
            <ServiceDescription>
              Our architecture services provide the blueprint for your technology initiatives, ensuring that all components work together harmoniously to achieve your business goals.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                System architecture design aligned with business objectives
              </ServiceFeature>
              <ServiceFeature>
                Technology selection and evaluation
              </ServiceFeature>
              <ServiceFeature>
                Scalability and performance planning
              </ServiceFeature>
              <ServiceFeature>
                Legacy system modernization strategies
              </ServiceFeature>
              <ServiceFeature>
                Architecture review and optimization
              </ServiceFeature>
            </ServiceFeatures>
            <CtaButton to="/contact">Discuss Your Architecture Needs</CtaButton>
          </ServiceContent>
        </ServiceContainer>
      </ServiceSection>
      
      <ServiceSection id="system-design">
        <ServiceContainer>
          <ServiceImageContainer>
            <ServiceImage style={{ backgroundImage: 'url("/images/system-design.jpg")' }} />
          </ServiceImageContainer>
          
          <ServiceContent>
            <ServiceTitle>System Design</ServiceTitle>
            <ServiceDescription>
              We design resilient, maintainable systems that stand up to evolving business needs and technical challenges, ensuring your technology foundation is solid for years to come.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                Distributed systems design and implementation
              </ServiceFeature>
              <ServiceFeature>
                Data architecture and modeling
              </ServiceFeature>
              <ServiceFeature>
                API design and integration strategies
              </ServiceFeature>
              <ServiceFeature>
                Infrastructure as code implementation
              </ServiceFeature>
              <ServiceFeature>
                DevOps pipeline design
              </ServiceFeature>
            </ServiceFeatures>
            <CtaButton to="/contact">Explore System Design Services</CtaButton>
          </ServiceContent>
        </ServiceContainer>
      </ServiceSection>
      
      <ServiceSection id="consulting">
        <ServiceContainer>
          <ServiceImageContainer>
            <ServiceImage style={{ backgroundImage: 'url("/images/consulting.jpg")' }} />
          </ServiceImageContainer>
          
          <ServiceContent>
            <ServiceTitle>Consulting</ServiceTitle>
            <ServiceDescription>
              Our consulting services provide expert guidance on technology decisions, implementation strategies, and best practices tailored to your unique context and challenges.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                Technology strategy development
              </ServiceFeature>
              <ServiceFeature>
                Technical due diligence and assessment
              </ServiceFeature>
              <ServiceFeature>
                Cloud migration planning and execution
              </ServiceFeature>
              <ServiceFeature>
                Security architecture and implementation
              </ServiceFeature>
              <ServiceFeature>
                Performance optimization and troubleshooting
              </ServiceFeature>
            </ServiceFeatures>
            <CtaButton to="/contact">Book a Consultation</CtaButton>
          </ServiceContent>
        </ServiceContainer>
      </ServiceSection>
      
      <ServiceSection id="transformation">
        <ServiceContainer>
          <ServiceImageContainer>
            <ServiceImage style={{ backgroundImage: 'url("/images/transformation.jpg")' }} />
          </ServiceImageContainer>
          
          <ServiceContent>
            <ServiceTitle>Transformation Strategy</ServiceTitle>
            <ServiceDescription>
              We help organizations navigate complex digital transformations, ensuring technology changes align with business goals and deliver measurable value.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                Digital transformation roadmapping
              </ServiceFeature>
              <ServiceFeature>
                Technology stack modernization
              </ServiceFeature>
              <ServiceFeature>
                Process automation and optimization
              </ServiceFeature>
              <ServiceFeature>
                Change management and team enablement
              </ServiceFeature>
              <ServiceFeature>
                Implementation planning and governance
              </ServiceFeature>
            </ServiceFeatures>
            <CtaButton to="/contact">Transform Your Technology</CtaButton>
          </ServiceContent>
        </ServiceContainer>
      </ServiceSection>
      
      <ProcessSection>
        <ProcessContainer>
          <ProcessTitle>Our Process</ProcessTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Discovery</StepTitle>
              <StepDescription>
                We begin by deeply understanding your business goals, technical challenges, and current state through collaborative workshops and analysis.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Strategy</StepTitle>
              <StepDescription>
                We develop a tailored approach that aligns technology decisions with business objectives, creating a clear roadmap for success.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Execution</StepTitle>
              <StepDescription>
                We implement solutions with precision and expertise, ensuring quality and alignment with the strategic vision at every step.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Evolution</StepTitle>
              <StepDescription>
                We help you adapt and evolve your technology as your business needs change, ensuring continued alignment and value.
              </StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </ProcessContainer>
      </ProcessSection>
      
      <TestimonialsSection>
        <TestimonialsContainer>
          <TestimonialsTitle>Client Success Stories</TestimonialsTitle>
          <TestimonialsGrid>
            <TestimonialCard>
              <TestimonialText>
                "Kriegerlabs transformed our complex technical challenges into a clear, actionable roadmap. Their architecture guidance saved us months of development time and helped us avoid costly mistakes."
              </TestimonialText>
              <TestimonialClient>
                <ClientImage style={{ backgroundImage: 'url("/images/client1.jpg")' }} />
                <ClientInfo>
                  <ClientName>Sarah Johnson</ClientName>
                  <ClientPosition>CTO, FinTech Innovations</ClientPosition>
                </ClientInfo>
              </TestimonialClient>
            </TestimonialCard>
            
            <TestimonialCard>
              <TestimonialText>
                "Working with Kriegerlabs gave us the confidence to make critical technology decisions. Their expertise in cloud architecture helped us scale our platform to handle 10x the traffic without increasing costs."
              </TestimonialText>
              <TestimonialClient>
                <ClientImage style={{ backgroundImage: 'url("/images/client2.jpg")' }} />
                <ClientInfo>
                  <ClientName>Michael Chen</ClientName>
                  <ClientPosition>VP of Engineering, DataStream</ClientPosition>
                </ClientInfo>
              </TestimonialClient>
            </TestimonialCard>
            
            <TestimonialCard>
              <TestimonialText>
                "The system design provided by Kriegerlabs became the foundation of our technical strategy. Three years later, it still serves us well as we continue to grow and evolve our product."
              </TestimonialText>
              <TestimonialClient>
                <ClientImage style={{ backgroundImage: 'url("/images/client3.jpg")' }} />
                <ClientInfo>
                  <ClientName>Jennifer Rodriguez</ClientName>
                  <ClientPosition>Founder, HealthTech Solutions</ClientPosition>
                </ClientInfo>
              </TestimonialClient>
            </TestimonialCard>
          </TestimonialsGrid>
        </TestimonialsContainer>
      </TestimonialsSection>
      
      <CtaSection>
        <CtaContainer>
          <CtaTitle>Ready to Transform Your Technical Vision?</CtaTitle>
          <CtaText>
            Let's discuss how we can help architect intelligent solutions for your organization's unique challenges.
          </CtaText>
          <CtaWhiteButton to="/contact">
            Start a Conversation
          </CtaWhiteButton>
        </CtaContainer>
      </CtaSection>
    </>
  );
};

export default Services;